import { useMemo } from "react";
import { Pressable, ScrollView } from "react-native";
import { useRouter, usePathname, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useCameras } from "@src/features/cameras";
import { useTenant } from "@src/features/tenants";
import { useTrafficFlowScenarios } from "@src/features/traffic-flow/hooks";
import { groupScenariosBySite, type ScenarioRow } from "@src/features/traffic-flow/utils/scenario-group";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import {
  useScenarioCoordinatesStore,
  EMPTY_SCENARIO_CONFIG,
} from "@src/features/traffic-flow/stores/scenario-coordinates-store";

interface SidepanelScenarioListProps {
  selectedSiteId?: string | null;
}

export function SidepanelScenarioList({ selectedSiteId }: SidepanelScenarioListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { theme } = useUnistyles();
  const { selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id ?? "";
  const configByUuid = useScenarioCoordinatesStore((s) => s.byTenant[tenantId] ?? EMPTY_SCENARIO_CONFIG);
  const { data: cameras, error: camerasError } = useCameras();
  const { data: scenarios, error: scenariosError } = useTrafficFlowScenarios();
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);
  const hasError = camerasError || scenariosError;

  const rows: ScenarioRow[] = useMemo(() => {
    if (!cameras || !scenarios) return [];
    // Pass configByUuid so friendly-name overrides reach the sidebar list,
    // matching the grid + map tooltips. Without it, the sidebar shows the
    // backend scenario name while every other surface shows the override.
    const groups = groupScenariosBySite(scenarios, cameras, configByUuid);
    const filtered = selectedSiteId ? groups.filter((g) => g.siteId === selectedSiteId) : groups;
    return filtered.flatMap((g) => g.rows);
  }, [cameras, scenarios, configByUuid, selectedSiteId]);

  const isOnTrafficFlow = pathname.endsWith("/traffic-flow");
  const { selectedScenario } = useLocalSearchParams<{ selectedScenario?: string }>();

  if (hasError && (!cameras || !scenarios)) {
    return (
      <View py="lg" alignItems="center">
        <Text textStyle="xs/regular" color="textMuted">
          Couldn&apos;t load scenarios
        </Text>
      </View>
    );
  }

  if (!cameras || !scenarios) {
    return (
      <View py="lg" alignItems="center">
        <Text textStyle="xs/regular" color="textMuted">
          Loading...
        </Text>
      </View>
    );
  }

  if (rows.length === 0) {
    return (
      <View py="lg" alignItems="center">
        <Text textStyle="xs/regular" color="textMuted">
          No scenarios
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
      {rows.map((row) => {
        const isRowSelected = isOnTrafficFlow && selectedScenario === row.scenarioUuid;
        return (
          <Pressable
            key={row.key}
            onPress={() => {
              if (!selectedTenant?.id) return;
              if (isRowSelected) {
                // Deselect: mirror the grid card toggle in
                // ScenarioGrid.web.tsx — drop the query, keep the
                // current pathname (and therefore the current site
                // scope and store value), and use replace so toggling
                // doesn't pollute history.
                router.replace(pathname as never);
                return;
              }
              // Selecting a new scenario navigates to that scenario's
              // site scope. Keep the store in sync with the URL so the
              // SiteDropdown and any other store-driven UI reflect the
              // site we're navigating to.
              setSelectedSiteId(row.siteId);
              const target = `/tenants/${selectedTenant.id}/sites/${row.siteId}/traffic-flow?selectedScenario=${row.scenarioUuid}`;
              router.push(target as never);
            }}
            style={[styles.row, isRowSelected && { backgroundColor: theme.colors.background }]}
            accessibilityRole="button"
            accessibilityState={{ selected: isRowSelected }}
          >
            <Icon name="motion" size={theme.spacing["4xl"]} color={theme.colors.icon} />
            <View flex={1}>
              <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                {row.scenarioName}
              </Text>
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {row.cameraDisplayName}
              </Text>
            </View>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create((theme) => ({
  scroll: {
    flex: 1,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
  },
}));
